import { useState } from "react";
import { 
  Target, 
  Flame, 
  Mic, 
  Users, 
  Calendar, 
  TrendingUp,
  BookOpen,
  Shield
} from "lucide-react";
import { cn } from "@/lib/utils";

const features = [
  {
    id: "goals",
    icon: Target,
    title: "Goal Mastery",
    description: "Set ambitious targets with deadlines. Track progress with visual bars. Celebrate every milestone.",
    highlight: "Transform dreams into daily actions",
  },
  {
    id: "grit",
    icon: Flame,
    title: "GRIT Points",
    description: "Every action earns points. Level up through ranks. Compete on leaderboards. Unlock achievements.",
    highlight: "Gamify your growth journey",
  },
  {
    id: "voice",
    icon: Mic,
    title: "MōdCoach",
    description: "Speak your goals into existence. Get real-time motivation. Your personal forge master awaits.",
    highlight: "Talk to your future self",
  },
  {
    id: "routines",
    icon: Calendar,
    title: "Forge Routines",
    description: "Build unbreakable morning and evening rituals. Stack habits that compound over time.",
    highlight: "Consistency creates champions",
  },
  {
    id: "journal",
    icon: BookOpen,
    title: "Reflection Journal",
    description: "Daily prompts to sharpen your mind. Weekly reviews to track transformation. AI-powered insights.",
    highlight: "Know thyself, grow thyself",
  },
  {
    id: "habits",
    icon: Shield,
    title: "Break Bad Habits",
    description: "Track your battles against vices. Build streaks of resistance. Identify triggers and conquer them.",
    highlight: "Become unshakeable",
  },
  {
    id: "progress",
    icon: TrendingUp,
    title: "Visual Progress",
    description: "See your transformation unfold. Upload photos. Compare stats. Witness your evolution.",
    highlight: "Data-driven discipline",
  },
  {
    id: "community",
    icon: Users,
    title: "Brotherhood",
    description: "Join challenges with warriors worldwide. Share wins. Get accountability. Rise together.",
    highlight: "Iron sharpens iron",
  },
];

export const FeatureShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(features[0]);

  return (
    <section className="py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-display tracking-wider mb-4">
            THE <span className="text-gradient-ember">COMPLETE</span> ECOSYSTEM
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Every tool you need to forge unbreakable discipline, in one unified platform.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => setActiveFeature(feature)}
              className={cn(
                "p-4 rounded-xl border transition-all duration-300 text-left group",
                activeFeature.id === feature.id
                  ? "border-primary bg-primary/10 shadow-ember"
                  : "border-border/50 bg-card/50 hover:border-primary/50 hover:bg-card"
              )}
            >
              <feature.icon 
                className={cn(
                  "w-6 h-6 mb-2 transition-colors",
                  activeFeature.id === feature.id ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                )} 
              />
              <div className="font-display text-sm md:text-base tracking-wide">
                {feature.title}
              </div>
            </button>
          ))}
        </div>

        {/* Active feature detail */}
        <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-card via-card to-primary/5 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 rounded-xl bg-primary/20 shadow-ember">
                <activeFeature.icon className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-display tracking-wide">
                  {activeFeature.title}
                </h3>
                <p className="text-primary text-sm font-medium">
                  {activeFeature.highlight}
                </p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl">
              {activeFeature.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
